'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { EventSaveResult } from '@/components/eventModal';
import { PdfThumbnailCaptureHost } from '@/components/ui/PdfThumbnailCaptureHost';
import { explicitCitationPageFromMedia } from '@/shared/functions';
import type { MediaSource, Slide } from '@/shared/types';
import { originBindingFromMedia, resolveUserImageUploadCaseId } from '@/shared/userImageStorage';
import {
  applyThumbnailToSlide,
  captureSeekSeconds,
  eventMediaNeedsThumbnail,
  generateVideoThumbnail,
  MAX_THUMBNAIL_BACKFILLS_PER_LOAD,
  thumbnailBackfillKey,
  thumbnailMediaKind,
  uploadThumbnail,
} from '@/shared/timelineThumbnails';

type BackfillItem = {
  slide: Slide;
  mediaIndex: number;
  media: MediaSource;
  key: string;
};

export type UseTimelineThumbnailBackfillArgs = {
  slides: Slide[] | undefined;
  timelineId: string | undefined;
  isEditableTimeline: boolean;
  loadingTimeline: boolean;
  caseId: string | undefined;
  cellId: string | null;
  accessToken: string | null;
  tenantFriendlyName?: string;
  updateEvent: (slide: Slide, shouldRefetchTimeline?: boolean) => Promise<EventSaveResult>;
};

function collectBackfillItems(slides: Slide[] | undefined): BackfillItem[] {
  if (!slides?.length) return [];
  const items: BackfillItem[] = [];
  for (const slide of slides) {
    if (!slide.id || slide.id === 'TITLE_SLIDE') continue;
    const mediaList = slide.media ?? [];
    mediaList.forEach((media, mediaIndex) => {
      if (!eventMediaNeedsThumbnail(media)) return;
      items.push({
        slide,
        mediaIndex,
        media,
        key: thumbnailBackfillKey(slide.id as string, mediaIndex, media),
      });
    });
  }
  return items;
}

/**
 * When an editable timeline is loaded, capture missing video/PDF event
 * thumbnails one at a time and persist them on those events.
 * Does not write ``timeline.media`` — an empty title-slide cover is a valid
 * user choice and must not be replaced from the first event on reload.
 */
export function useTimelineThumbnailBackfill(
  args: UseTimelineThumbnailBackfillArgs,
): { pdfCaptureHost: ReactNode } {
  const {
    slides,
    timelineId,
    isEditableTimeline,
    loadingTimeline,
    caseId,
    cellId,
    accessToken,
  } = args;

  const [pdfJobMedia, setPdfJobMedia] = useState<MediaSource | null>(null);
  const pdfResolverRef = useRef<((dataUrl: string | null) => void) | null>(null);
  const attemptedRef = useRef<Set<string>>(new Set());
  const timelineIdForAttemptedRef = useRef<string | undefined>(undefined);
  const runGenRef = useRef(0);
  const argsRef = useRef(args);
  argsRef.current = args;

  const capturePdfThumbnail = (media: MediaSource) =>
    new Promise<string | null>((resolve) => {
      let settled = false;
      const finish = (dataUrl: string | null) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeoutId);
        pdfResolverRef.current = null;
        setPdfJobMedia(null);
        resolve(dataUrl);
      };
      const timeoutId = window.setTimeout(() => finish(null), 30_000);
      pdfResolverRef.current = finish;
      setPdfJobMedia(media);
    });

  useEffect(() => {
    if (timelineIdForAttemptedRef.current !== timelineId) {
      attemptedRef.current = new Set();
      timelineIdForAttemptedRef.current = timelineId;
    }
  }, [timelineId]);

  const slidesLength = slides?.length ?? 0;

  useEffect(() => {
    const enabled =
      isEditableTimeline &&
      !loadingTimeline &&
      !!timelineId &&
      slidesLength > 0 &&
      !!caseId?.trim() &&
      !!cellId &&
      !!accessToken;

    if (!enabled) return;

    const gen = ++runGenRef.current;
    const isCurrent = () => runGenRef.current === gen;

    const persistThumbnail = async (item: BackfillItem) => {
      const latest = argsRef.current;
      const kind = thumbnailMediaKind(item.media);
      if (!kind) return;

      let imageData: string | null = null;
      if (kind === 'video') {
        imageData = await generateVideoThumbnail(item.media);
      } else {
        imageData = await capturePdfThumbnail(item.media);
      }
      if (!isCurrent() || !imageData) return;

      const { sourceKey, sourceMd5 } = originBindingFromMedia(item.media);
      const caseIdForUpload = resolveUserImageUploadCaseId({
        selectedCaseId: latest.caseId,
        media: item.media,
        sourceKey,
      });
      if (!caseIdForUpload || !latest.cellId || !latest.accessToken) return;

      const imageRef = await uploadThumbnail({
        imageData,
        purpose: kind === 'video' ? 'video-still-frame' : 'pdf-page-thumbnail',
        caseId: caseIdForUpload,
        cellId: latest.cellId,
        accessToken: latest.accessToken,
        tenantFriendlyName: latest.tenantFriendlyName,
        sourceKey,
        sourceMd5,
      });
      if (!isCurrent()) return;

      const currentSlide =
        latest.slides?.find((slide) => slide.id === item.slide.id) ?? item.slide;
      const nextSlide = applyThumbnailToSlide(
        currentSlide,
        item.mediaIndex,
        imageRef,
        kind,
        kind === 'video'
          ? { stillFrameTime: String(captureSeekSeconds(item.media)) }
          : { pageNumber: explicitCitationPageFromMedia(item.media) },
      );
      await latest.updateEvent(nextSlide, false);
    };

    void (async () => {
      let processed = 0;
      while (isCurrent() && processed < MAX_THUMBNAIL_BACKFILLS_PER_LOAD) {
        const pending = collectBackfillItems(argsRef.current.slides).filter(
          (item) => !attemptedRef.current.has(item.key),
        );
        const item = pending[0];
        if (!item) break;
        attemptedRef.current.add(item.key);
        processed += 1;
        try {
          await persistThumbnail(item);
        } catch (error) {
          console.warn(
            'Failed to auto-generate timeline thumbnail:',
            error instanceof Error ? error.message : error,
          );
        }
        if (!isCurrent()) {
          attemptedRef.current.delete(item.key);
          return;
        }
      }
    })();

    return () => {
      runGenRef.current += 1;
      pdfResolverRef.current?.(null);
    };
  }, [
    slidesLength,
    timelineId,
    isEditableTimeline,
    loadingTimeline,
    caseId,
    cellId,
    accessToken,
  ]);

  return {
    pdfCaptureHost: (
      <PdfThumbnailCaptureHost
        media={pdfJobMedia}
        onCaptured={(dataUrl) => {
          pdfResolverRef.current?.(dataUrl);
        }}
      />
    ),
  };
}
